/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, Grid, Heading, InfoLabel, Text, VFlow } from 'bold-ui'
import { Breadcrumb } from 'components/breadcrumb'
import { PageContent } from 'components/layout/PageContent'
import { LotacaoResponsavelCard } from 'components/lotacao-responsavel/LotacaoResponsavelCard'
import { TitleGroup } from 'components/TitleGroup'
import { Fragment } from 'react'
import { Redirect } from 'react-router'
import { AtividadeColetivaInfoHeader } from 'view/atividade-coletiva/componentes/AtividadeColetivaInfoHeader'
import { LotacoesEnvolvidasTable } from 'view/atividade-coletiva/componentes/LotacoesEnvolvidasTable'
import { ParticipantesTable } from 'view/atividade-coletiva/componentes/participantes/ParticipantesTable'
import {
  AtividadeColetivaSaudeModel,
  AtividadePseModel,
  praticaSaudeRecord,
  publicoAlvoRecord,
  temaSaudeRecord,
  tipoAtividadeRecord,
  TIPOS_ATIVIDADE_EM_SAUDE,
} from 'view/atividade-coletiva/model-atividadeColetiva'

import { isOnlyEducacaoPse, isPraticaSaudePnct } from '../utils-atividadeColetivaSaude'

interface AtividadeColetivaVisualizarSaudeViewProps {
  atividadeColetiva: AtividadeColetivaSaudeModel
  isFonteDadosPec: boolean
}

export const AtividadeColetivaVisualizarSaudeView = (props: AtividadeColetivaVisualizarSaudeViewProps) => {
  const { atividadeColetiva, isFonteDadosPec } = props

  if (!TIPOS_ATIVIDADE_EM_SAUDE.includes(atividadeColetiva.tipoAtividade)) {
    return <Redirect to='/404' />
  }

  const anotacoesTextOnly = atividadeColetiva?.anotacoes?.removeTags()

  const atividadePse: AtividadePseModel = {
    saude: atividadeColetiva?.atividadeSaudePse,
    educacao: atividadeColetiva?.atividadeEducacaoPse,
  }

  return (
    <Fragment>
      <Breadcrumb title='Visualização de registro de atividade coletiva em saúde' />
      <PageContent type='filled' style={styles.pageContent}>
        <TitleGroup title='Registro de atividade coletiva em saúde' style={styles.header} />

        <Grid gap={2}>
          <Cell size={12}>
            <VFlow vSpacing={0.5}>
              <Heading level={3}>Responsabilidade de registro</Heading>
              <LotacaoResponsavelCard responsavel={atividadeColetiva.lotacaoResponsavel} />
            </VFlow>
          </Cell>

          <Cell size={12}>
            <AtividadeColetivaInfoHeader atividadeColetiva={atividadeColetiva} isFonteDadosPec={isFonteDadosPec} />
          </Cell>

          <Cell size={12} style={styles.section}>
            <Heading level={2} style={styles.title}>
              Profissionais envolvidos
            </Heading>
          </Cell>

          <Cell size={12}>
            {isOnlyEducacaoPse({ atividadePse }) ? (
              'Não é possível cadastrar profissionais para atividades de educação do Programa Saúde na Escola.'
            ) : (
              <LotacoesEnvolvidasTable items={atividadeColetiva.lotacoesEnvolvidas ?? []} />
            )}
          </Cell>

          <Cell size={12} style={styles.section}>
            <Heading level={2} style={styles.title}>
              Dados da atividade
            </Heading>
          </Cell>

          <Cell size={4}>
            <InfoLabel title='Tipo de atividade'>{tipoAtividadeRecord[atividadeColetiva.tipoAtividade]}</InfoLabel>
          </Cell>

          <Cell size={4}>
            <InfoLabel title='Público alvo'>
              {atividadeColetiva.publicoAlvo
                ?.map((alvo) => publicoAlvoRecord[alvo])
                .sort((a, b) => a.localeCompare(b))
                .join(', ')}
            </InfoLabel>
          </Cell>

          <Cell size={4}>
            <InfoLabel title='Temas para a saúde'>
              {atividadeColetiva.temasSaude
                ?.map((tema) => temaSaudeRecord[tema])
                .sort((a, b) => a.localeCompare(b))
                .join(', ')}
            </InfoLabel>
          </Cell>

          <Cell size={4}>
            <InfoLabel title='Práticas em saúde'>
              {atividadeColetiva.praticasSaude
                ?.map((pratica) => praticaSaudeRecord[pratica])
                .sort((a, b) => a.localeCompare(b))
                .join(', ')}
            </InfoLabel>
          </Cell>

          <Cell size={8}>
            <InfoLabel title='Código do SIGTAP'>
              {atividadeColetiva.procedimento &&
                `${atividadeColetiva.procedimento.descricao} - ${atividadeColetiva.procedimento.codigo}`}
            </InfoLabel>
          </Cell>

          {isFonteDadosPec && (
            <Cell size={12} style={styles.wordBreakSection}>
              <InfoLabel title='Anotações da atividade'>
                {anotacoesTextOnly && <Text dangerouslySetInnerHTML={{ __html: atividadeColetiva.anotacoes }} />}
              </InfoLabel>
            </Cell>
          )}

          <Cell size={12} style={styles.section}>
            <Heading level={2} style={styles.title}>
              Participantes
            </Heading>
          </Cell>

          <Cell size={12}>
            <ParticipantesTable
              items={atividadeColetiva.participantes ?? []}
              dataAtividade={atividadeColetiva.dataAtividade}
              isPraticaSaudePnct={isPraticaSaudePnct(atividadeColetiva.praticasSaude)}
            />
          </Cell>
        </Grid>
      </PageContent>
    </Fragment>
  )
}

const styles = {
  header: css`
    margin-top: 1rem;
    margin-bottom: 2rem;
  `,
  section: css`
    margin-top: 1.5rem;
  `,
  title: css`
    margin-bottom: 0.5rem;
  `,
  pageContent: css`
    padding-bottom: 3.5rem;
  `,
  wordBreakSection: css`
    word-break: break-all;
  `,
}
